import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { match, Pattern } from 'ts-pattern';

import { useLogin } from '~/api/auth';
import { FetchError, HttpBusinessConflictError, HttpError, HttpTooManyRequestsError } from '~/api/errors';
import { Alert, Button, ErrorMessage, ExternalLink, Link, Separator } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { EMAIL } from '~/constants/regex';
import { routes } from '~/providers/RouterProvider/router.routes';
import { isNotBlank } from '~/utils/string';

import { AuthLayoutContent } from '../AuthLayout/AuthLayoutContent';
import { AuthLayoutTitleGroup } from '../AuthLayout/AuthLayoutTitleGroup';
import { AuthLink } from '../AuthLink/AuthLink';
import { ResendVerificationEmail } from '../ResendVerificationEmail/ResendVerificationEmail';
import { forgotPasswordLinkStyle, formStyle } from './Login.css';

type LoginFormType = {
  emailAddress: string;
  password: string;
};

export const Login = () => {
  const mutation = useLogin();

  const { control, handleSubmit } = useForm<LoginFormType>({
    defaultValues: {
      emailAddress: '',
      password: '',
    },
  });

  // Snapshot of the email address input field at the time of submit,
  // which is used to resend the verification email.
  const [emailAddressSnapshot, setEmailAddressSnapshot] = useState('');

  const [autoAnimateRef] = useAutoAnimate();
  const { state } = useLocation();
  const { t } = useTranslation(['auth', 'common', 'validation']);

  const onSubmit = handleSubmit((data) => {
    setEmailAddressSnapshot(data.emailAddress);

    mutation.mutate(data);
  });

  return (
    <AuthLayoutContent>
      <AuthLayoutTitleGroup
        eyebrow={t('auth:loginPage.header.eyebrow')}
        subtitle={t('auth:loginPage.header.subtitle')}
        title={t('auth:loginPage.header.title')}
      />

      {state?.isSessionExpired === true && (
        <Alert announce icon="Warning" status="warning" title={t('auth:loginPage.sessionExpiredAlert.title')}>
          {t('auth:loginPage.sessionExpiredAlert.description')}
        </Alert>
      )}

      <Form className={formStyle} onSubmit={onSubmit} ref={autoAnimateRef}>
        <Controller
          control={control}
          name="emailAddress"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label>{t('auth:loginPage.form.emailField.label')}</Field.Label>
              <Input onBlur={onBlur} onValueChange={onChange} ref={ref} type="email" value={value} />
              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{
            required: { value: true, message: t('validation:required') },
            validate: { notBlank: (value) => isNotBlank(value) || t('validation:required') },
            pattern: { value: EMAIL, message: t('validation:email.invalid') },
          }}
        />

        <Controller
          control={control}
          name="password"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label>{t('auth:loginPage.form.passwordField.label')}</Field.Label>
              <Input onBlur={onBlur} onValueChange={onChange} ref={ref} type="password" value={value} />
              <Field.Error match={!!error}>{error?.message}</Field.Error>

              <Link className={forgotPasswordLinkStyle} to={routes.forgotPassword}>
                {t('auth:loginPage.forgotPasswordLink')}
              </Link>
            </Field.Root>
          )}
          rules={{
            required: { value: true, message: t('validation:required') },
          }}
        />

        {mutation.isError &&
          match(mutation.error)
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'IneligibleUserCannotLogIn',
              () => (
                <Alert announce icon="Info" status="info" title={t('auth:loginPage.errors.ineligible.title')}>
                  {t('auth:loginPage.errors.ineligible.description')}
                </Alert>
              ),
            )
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'UserWithUnverifiedEmailCannotLogIn',
              () => (
                <Alert announce icon="Info" status="info" title={t('auth:loginPage.errors.unverifiedEmail.title')}>
                  {t('auth:loginPage.errors.unverifiedEmail.description')}
                  <Separator />
                  <ResendVerificationEmail emailAddress={emailAddressSnapshot} />
                </Alert>
              ),
            )
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'RejectedUserCannotLogIn',
              () => (
                <Alert announce icon="Error" status="danger" title={t('auth:loginPage.errors.rejected.title')}>
                  {t('auth:loginPage.errors.rejected.description')}
                </Alert>
              ),
            )
            .with(
              Pattern.instanceOf(HttpBusinessConflictError),
              (error) => error.errorCode === 'LoginInvalidEmailOrPassword',
              () => <ErrorMessage>{t('auth:loginPage.errors.invalidEmailOrPassword')}</ErrorMessage>,
            )
            .with(Pattern.instanceOf(HttpTooManyRequestsError), () => <ErrorMessage>{t('common:errors.tooManyRequests')}</ErrorMessage>)
            .with(Pattern.instanceOf(HttpError), () => (
              <ErrorMessage>
                <Trans components={{ email: <ExternalLink /> }} i18nKey="common:errors.httpErrorWithLink" />
              </ErrorMessage>
            ))
            .with(Pattern.instanceOf(FetchError), () => <ErrorMessage>{t('common:errors.fetchError')}</ErrorMessage>)
            .otherwise(() => <ErrorMessage>{t('common:errors.unknown')}</ErrorMessage>)}

        <Button icon="ArrowRight" iconPlacement="right" loading={mutation.isPending} type="submit">
          {t('auth:loginPage.form.submitButton')}
        </Button>
      </Form>

      <Separator text={t('common:or')} />

      <AuthLink to="registration" />
    </AuthLayoutContent>
  );
};
